import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Target, FloppyDisk, Warning, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HelpLink from "@/components/HelpLink";
import { SitePixelsAdmin } from "@/components/pixels/SitePixelsAdmin";
import { PixelCheck } from "@/components/pixels/PixelCheck";
import { HIGHLIGHT, focusFixTarget } from "@/components/pixels/pixelFix";

/**
 * Iter-45: Global marketing pixel configuration.
 *
 * Four independent providers so admins can wire each ad platform without
 * cross-contamination. Individual landing pages can override any of these
 * values via `pixel_overrides` on the LP editor.
 *
 * Access: internal admins + media_buyer role (see routes/pixels_routes.py).
 */
const PROVIDERS = [
  {
    key: "meta_pixel_id",
    label: "Meta (Facebook / Instagram)",
    hint: "Pixel ID from Meta Events Manager (e.g. 1234567890123456)",
    placeholder: "1234567890123456",
    docsUrl: "https://business.facebook.com/events_manager",
    example: "Fires PageView on load and standard Lead event on submit",
  },
  {
    key: "google_ads_id",
    label: "Google Ads (base tag)",
    hint: "Conversion ID from Google Ads (starts with AW-)",
    placeholder: "AW-123456789",
    docsUrl: "https://ads.google.com/aw/conversions",
    example: "Loads gtag.js so conversions can fire",
  },
  {
    key: "google_ads_conversion_label",
    label: "Google Ads (conversion label)",
    hint: "Paired with the ID above — the send_to key from the conversion",
    placeholder: "abcDEF123",
    docsUrl: "https://ads.google.com/aw/conversions",
    example: "Fires when the lead form is successfully submitted",
  },
  {
    key: "ga4_measurement_id",
    label: "Google Analytics 4",
    hint: "Measurement ID from GA4 (starts with G-)",
    placeholder: "G-XXXXXXXX",
    docsUrl: "https://analytics.google.com",
    example: "Fires generate_lead event on submit",
  },
  {
    key: "tiktok_pixel_id",
    label: "TikTok",
    hint: "TikTok Events Manager pixel ID (starts with C)",
    placeholder: "CXXXXXXXXXXXXXX",
    docsUrl: "https://ads.tiktok.com/i18n/events_manager",
    example: "Fires SubmitForm event on submit",
  },
];

const EMPTY = {
  meta_pixel_id: "",
  google_ads_id: "",
  google_ads_conversion_label: "",
  ga4_measurement_id: "",
  tiktok_pixel_id: "",
};

export default function AdminPixels() {
  const { user } = useAuth();
  const [config, setConfig] = useState(EMPTY);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [params] = useSearchParams();
  const fixField = params.get("fix");

  useEffect(() => {
    if (!loading && fixField) focusFixTarget(`[data-testid="pixel-input-${fixField}"]`);
  }, [loading, fixField]);

  useEffect(() => {
    api.get("/admin/pixels")
      .then((r) => {
        const c = r.data?.config || {};
        setConfig({
          meta_pixel_id: c.meta_pixel_id || "",
          google_ads_id: c.google_ads_id || "",
          google_ads_conversion_label: c.google_ads_conversion_label || "",
          ga4_measurement_id: c.ga4_measurement_id || "",
          tiktok_pixel_id: c.tiktok_pixel_id || "",
        });
        setMeta({ updated_at: r.data?.updated_at, updated_by: r.data?.updated_by });
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load pixels"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...config };
      // Empty strings → null so the backend clears the field.
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === "string" && !payload[k].trim()) payload[k] = null;
      });
      const r = await api.patch("/admin/pixels", payload);
      toast.success("Marketing pixels saved — live on all published landing pages");
      setMeta({ updated_at: r.data?.updated_at, updated_by: user?.id });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = Object.values(config).filter(
    (v) => typeof v === "string" && v.trim(),
  ).length;

  return (
    <div className="p-2 max-w-4xl" data-testid="admin-pixels">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <Target size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">
          Marketing pixels
        </h1>
        <div className="label-mono ml-2">Global defaults for every landing page</div>
        <div className="ml-auto"><HelpLink slug="admin-pixels" label="Pixels guide" testId="help-link-pixels" /></div>
      </div>
      <p className="text-sm text-zinc-600 mb-6 max-w-3xl">
        Configure Meta, Google Ads, GA4, and TikTok pixels once — every
        landing page fires them on load and reports a{" "}
        <strong>Lead conversion event</strong> when the form submits.
        Buyer pages can add their own pixels; those load on top and never replace these.
      </p>

      <PixelCheck />

      <div className="brut-card p-4 mb-6 bg-zinc-50 flex items-center gap-3 flex-wrap" data-testid="pixel-status-bar">
        {activeCount > 0 ? (
          <>
            <CheckCircle size={16} weight="fill" className="text-emerald-700" />
            <div className="text-sm">
              <span className="font-bold">{activeCount}</span> of {PROVIDERS.length} providers active
            </div>
          </>
        ) : (
          <>
            <Warning size={16} weight="fill" className="text-amber-700" />
            <div className="text-sm">
              No pixels configured yet — conversions are not being tracked.
            </div>
          </>
        )}
        {meta.updated_at && (
          <div className="label-mono ml-auto">
            Last updated {new Date(meta.updated_at).toLocaleString()}
          </div>
        )}
      </div>

      {loading ? (
        <div className="label-mono text-center py-10">Loading…</div>
      ) : (
        <div className="space-y-4">
          {PROVIDERS.map((p) => {
            const val = config[p.key];
            const active = typeof val === "string" && val.trim().length > 0;
            return (
              <div
                key={p.key}
                className={
                  "brut-card p-5 border-2 " +
                  (active ? "border-emerald-700" : "border-black") +
                  (p.key === fixField ? ` ${HIGHLIGHT}` : "")
                }
                data-testid={`pixel-row-${p.key}`}
                data-highlighted={p.key === fixField ? "true" : undefined}
              >
                {p.key === fixField && (
                  <div className="mb-2 text-[11px] font-bold text-amber-800" data-testid="pixel-fix-hint">
                    Fix this field, then press Save marketing pixels below.
                  </div>
                )}
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div>
                    <div className="font-chivo font-black uppercase tracking-tighter text-lg">
                      {p.label}
                    </div>
                    <div className="text-xs text-zinc-600 mt-1">{p.hint}</div>
                  </div>
                  <span
                    className={
                      "text-[10px] uppercase tracking-widest px-2 py-0.5 border " +
                      (active
                        ? "bg-emerald-700 border-emerald-700 text-white"
                        : "bg-zinc-100 border-zinc-500 text-zinc-600")
                    }
                    data-testid={`pixel-status-${p.key}`}
                  >
                    {active ? "Active" : "Not set"}
                  </span>
                </div>
                <input
                  value={config[p.key]}
                  onChange={(e) => set(p.key, e.target.value)}
                  placeholder={p.placeholder}
                  data-testid={`pixel-input-${p.key}`}
                  className="w-full border border-black px-3 py-2 text-sm font-mono"
                />
                <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-3 flex-wrap">
                  <span>→ {p.example}</span>
                  <a
                    href={p.docsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-black"
                  >
                    Find my ID
                  </a>
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-black">
            <button
              onClick={save}
              disabled={saving}
              data-testid="pixel-save"
              className="border-2 border-black bg-black text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-emerald-700 hover:border-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            >
              <FloppyDisk size={14} weight="bold" />
              {saving ? "Saving…" : "Save marketing pixels"}
            </button>
          </div>
        </div>
      )}

      <SitePixelsAdmin />

      <div className="mt-8 p-4 border border-black bg-amber-50 text-xs leading-relaxed">
        <div className="font-bold uppercase tracking-widest text-[10px] mb-2">
          What these pixels do on your landing pages
        </div>
        <ul className="space-y-1">
          <li>
            <b>Page load:</b> fires the base pixel + PageView / page_view event for
            each configured provider.
          </li>
          <li>
            <b>Form submit:</b> automatically fires the standard Lead conversion
            event for every configured provider:
            <ul className="ml-4 mt-1 text-[11px] font-mono">
              <li>Meta: fbq(&apos;track&apos;, &apos;Lead&apos;)</li>
              <li>Google Ads: gtag(&apos;event&apos;, &apos;conversion&apos;, ...)</li>
              <li>GA4: gtag(&apos;event&apos;, &apos;generate_lead&apos;)</li>
              <li>TikTok: ttq.track(&apos;SubmitForm&apos;)</li>
            </ul>
          </li>
          <li>
            <b>Always on:</b> these main pixels load on every landing and thank-you
            page. A page or firm can add its own pixels on top from the landing page
            editor; they never replace yours.
          </li>
        </ul>
      </div>
    </div>
  );
}
